import {
    handleCreateBusiness,
  handleFetchBusinessById,
    handleSwitchCurrentBusiness,
} from "api/onboarding";
import OnboardingLayout from "component/auth/OnboardingLayout";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import MultiSelectDropdown from "component/form/MultiSelectDropdown";
import Loader from "component/loader";
import React from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowIcon, CheckCircleIcon, SearchIcon } from "asset/icons";
import { checkAutoLogin } from "store/authServices";
import { updateBusinessDetails } from "api/fetchers";
import useKybCountries from "hooks/useKybCountries";

const steps = [
    { title: "Business information", subtitles: ["Business details", "Contact details"] },
    { title: "Verify your business" },
    { title: "Add directors" },
];

// Business type options with descriptions
export const businessTypeOptions = [
    {
        label: (
            <div className="flex flex-col">
                <span className="aku-body-normal-medium">Limited Liability Company (LLC)</span>
                <span className="aku-body-normal-regular text-[#667085]">
                    Registered corporations
                </span>
            </div>
        ),
        value: "LLC",
        title: "Limited Liability Company (LLC)",
    },
    {
        label: (
            <div className="flex flex-col">
                <span className="aku-body-normal-medium">Sole Proprietorship or Partnerships</span>
                <span className="aku-body-normal-regular text-[#667085]">
                    Registered businesses or partnerships
                </span>
            </div>
        ),
        value: "Sole Proprietor",
        title: "Sole Proprietorship or Partnerships",
    },
];

export const natureOfBusinessOptions = [
    {
        label: "Agro commodities",
        value: "AGRO_COMMODITIES",
        nature: "AGRO_COMMODITIES",
        code: "AGRO_COMMODITIES",
    },
    { label: "Equipment", value: "EQUIPMENT", nature: "EQUIPMENT", code: "EQUIPMENT" },
    {
        label: "Trucking & Haulage services",
        value: "TRUCKING_AND_HAULAGE_SERVICES",
        nature: "TRUCKING_AND_HAULAGE_SERVICES",
        code: "TRUCKING_AND_HAULAGE_SERVICES",
    },
    {
        label: "Shipping lines",
        value: "SHIPPING_LINES",
        nature: "SHIPPING_LINES",
        code: "SHIPPING_LINES",
    },
    {
        label: "Warehousing & Storage",
        value: "WAREHOUSING_AND_STORAGE",
        nature: "WAREHOUSING_AND_STORAGE",
        code: "WAREHOUSING_AND_STORAGE",
    },
    {
        label: "Exporter & Importer",
        value: "EXPORTER_AND_IMPORTER",
        nature: "EXPORTER_AND_IMPORTER",
        code: "EXPORTER_AND_IMPORTER",
    },
    {
        label: "Raw materials",
        value: "RAW_MATERIALS",
        nature: "RAW_MATERIALS",
        code: "RAW_MATERIALS",
    },
    {
        label: "Manufacturing",
        value: "MANUFACTURING",
        nature: "MANUFACTURING",
        code: "MANUFACTURING",
    },
    { label: "Other", value: "OTHER", nature: "OTHER", code: "OTHER" },

];

const highRiskActivityOptions = [
    {
        label: "Money Services (i.e., check cashing, gift cards, ATMs, remittances)",
        value: "Money Services",
    },
    { label: "Lending or Banking", value: "Lending or Banking" },
    {
        label: "Operate Foreign Exchange, Virtual Currencies Brokerage, or OTC",
        value: "Operate Foreign Exchange, Virtual Currencies Brokerage, or OTC",
    },
    { label: "Hold Client Funds (i.e., escrow)", value: "Hold Client Funds" },
    { label: "Safe Deposit Box Rentals", value: "Safe Deposit Box Rentals" },
    { label: "Marijuana or Related Services", value: "Marijuana or Related Services" },
    { label: "Tobacco or Nicotine Products", value: "Tobacco or Nicotine Products" },
    { label: "Pharmaceuticals", value: "Pharmaceuticals" },
    { label: "Third-Party Payment Processing", value: "Third-Party Payment Processing" },
    { label: "Adult Entertainment", value: "Adult Entertainment" },
    { label: "Weapons, Firearms, and Explosives", value: "Weapons, Firearms, and Explosives" },
    { label: "Gambling", value: "Gambling" },
    { label: "Other", value: "Other" },
];

const taxIdentificationTypeOptions = [
    { label: "EIN", value: "EIN" },
    { label: "TIN", value: "TIN" },
];

const registrationOrTaxIdOptions = [
    { label: "Registration number", value: "registrationNumber" },
    { label: "Tax ID Number", value: "taxIdentificationNumber" },
];

const CountryDropdown = ({
    value,
    options,
    onChange,
    loading,
    label = "Country of registration",
    error,
}) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [menuStyle, setMenuStyle] = React.useState({});
    const wrapperRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const selectedCountry = options.find((option) => option.value === value);
    const filteredOptions = options.filter((option) =>
        option.value.toLowerCase().includes(search.trim().toLowerCase())
    );

    const updateMenuPosition = React.useCallback(() => {
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const viewportPadding = 16;
        const menuGap = 8;
        const menuWidth = Math.min(rect.width, viewportWidth - viewportPadding * 2);
        const left = Math.min(
            Math.max(rect.left, viewportPadding),
            viewportWidth - menuWidth - viewportPadding
        );
        const desiredHeight = Math.min(Math.max(filteredOptions.length * 48 + 84, 180), 360);
        const spaceBelow = viewportHeight - rect.bottom - viewportPadding - menuGap;
        const spaceAbove = rect.top - viewportPadding - menuGap;
        const shouldOpenUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
        const availableHeight = Math.max(shouldOpenUpward ? spaceAbove : spaceBelow, 120);
        const maxHeight = Math.min(desiredHeight, availableHeight);

        setMenuStyle({
            left,
            maxHeight,
            top: shouldOpenUpward
                ? Math.max(viewportPadding, rect.top - menuGap - maxHeight)
                : Math.min(rect.bottom + menuGap, viewportHeight - viewportPadding),
            width: menuWidth,
        });
    }, [filteredOptions.length]);

    React.useEffect(() => {
        const handleOutsideClick = (event) => {
            const clickedTrigger = wrapperRef.current?.contains(event.target);
            const clickedMenu = menuRef.current?.contains(event.target);

            if (!clickedTrigger && !clickedMenu) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    React.useEffect(() => {
        if (!open) return;

        updateMenuPosition();
        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);

        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
        };
    }, [open, updateMenuPosition]);

    const handleSelect = (country) => {
        onChange(country.value);
        setSearch("");
        setOpen(false);
    };

    const menu =
        open &&
        createPortal(
            <div
                ref={menuRef}
                style={menuStyle}
                className="fixed z-[10000] overflow-hidden rounded-xl border border-[#E6E8F9] bg-white p-3 shadow-[-16px_-16px_16px_-4px_#10182808,16px_16px_32px_-4px_#10182814]"
            >
                <div className="flex h-12 items-center gap-3 rounded-lg bg-[#F4F2FC] px-4">
                    <SearchIcon className="size-5 *:stroke-[#6F739B]" />
                    <input
                        autoFocus
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search"
                        className="aku-body-normal-regular h-full w-full bg-transparent text-[#101828] outline-none placeholder:text-[#6F739B]"
                    />
                </div>

                <div
                    className="mt-3 overflow-y-auto pr-1"
                    style={{ maxHeight: Math.max((menuStyle.maxHeight || 180) - 72, 96) }}
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((country) => {
                            const active = country.value === value;

                            return (
                                <button
                                    key={country.code || country.value}
                                    type="button"
                                    onClick={() => handleSelect(country)}
                                    className={`aku-body-normal-medium flex h-12 w-full items-center gap-3 rounded-lg px-2 text-left text-[#101828] transition-colors ${
                                        active ? "bg-[#F4F2FC]" : "hover:bg-[#F8F7FF]"
                                    }`}
                                >
                                    <img
                                        src={country.imageUrl}
                                        alt={`${country.value} flag`}
                                        className="size-5 rounded-full object-cover"
                                    />
                                    <span className="min-w-0 flex-1 truncate">{country.value}</span>
                                    {active && <CheckCircleIcon className="size-5 shrink-0" />}
                                </button>
                            );
                        })
                    ) : (
                        <p className="aku-body-normal-medium px-2 py-4 text-[#6F739B]">
                            No countries found.
                        </p>
                    )}
                </div>
            </div>,
            document.body
        );

    return (
        <div ref={wrapperRef} className="relative">
            <p className="aku-body-small-medium mb-1 text-main-dark">{label}</p>
            <button
                type="button"
                disabled={loading}
                onClick={() => setOpen((prev) => !prev)}
                className={`aku-body-normal-regular flex h-[56px] w-full items-center justify-between rounded-lg px-4 text-left text-[#101828] disabled:cursor-not-allowed disabled:opacity-70 ${
                    error ? "bg-[#FFF1F3] ring-1 ring-[#F04438]" : "bg-[#f1f5f9]"
                }`}
            >
                <span className="flex min-w-0 items-center gap-3">
                    {selectedCountry?.imageUrl && (
                        <img
                            src={selectedCountry.imageUrl}
                            alt={`${selectedCountry.value} flag`}
                            className="size-5 rounded-full object-cover"
                        />
                    )}
                    <span className={selectedCountry ? "truncate" : "truncate text-[#8A8FA6]"}>
                        {loading
                            ? "Loading countries..."
                            : selectedCountry?.value || "Select country"}
                    </span>
                </span>
                <ArrowIcon className="size-3.5 shrink-0 *:stroke-[#6F739B]" />
            </button>
            {error && <p className="mt-1 text-xs text-[#D92D20]">{error}</p>}
            {menu}
        </div>
    );
};

const BusinessDetails = ({ currentStep = 0, onBack }) => {
    const [formData, setFormData] = React.useState({
        country: "",
        countryCode: "",
        businessType: "",
        rcNumber: "",
        businessName: "",
        tin: "",
        taxIdentificationType: "EIN",
        registrationOrTaxIdType: "registrationNumber",
        natureOfBusiness: "",
        businessDescription: "",
        engagesInHighRiskActivities: "",
        highRiskActivities: [],
    });

  const { data: kybCountriesData, isLoading: countriesLoading } = useKybCountries();
  const countryOptions = kybCountriesData?.countryOptions || [];
  const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [rcNumberError, setRcNumberError] = React.useState("");
    const [tinError, setTinError] = React.useState("");
    const [validationErrors, setValidationErrors] = React.useState({});
    const dispatch = useDispatch();
    const businessId = useSelector((state) => state.businessid);
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary = new URLSearchParams(location.search).get("returnTo") === "summary";

    React.useEffect(() => {
        if (!shouldReturnToSummary || !businessId) return;

        let ignore = false;
        const prefillBusiness = async () => {
            try {
                const response = await handleFetchBusinessById(businessId);
                const business = response?.business || response;
                if (ignore || !business) return;

                const countryName = business?.businessCountry?.name ||
                    (typeof business?.country === "string" ? business.country : business?.country?.name) || "";
                const countryCode = business?.businessCountry?.code || business?.countryCode || "";
                const registrationNumber = business?.registrationNumber?.value ||
                    business?.registrationNumberDetails?.value || business?.registrationNumber || "";
                const taxValue = business?.EIN?.value || business?.EIN || business?.TIN?.value || business?.TIN || "";
                const nature = business?.natureOfBusiness?.code || business?.natureOfBusiness?.nature || business?.natureOfBusiness || "";

                setFormData({
                    country: countryName,
                    countryCode,
                    businessType: business?.type === "SoleProprietorship" ? "Sole Proprietor" : business?.type || "",
                    rcNumber: String(registrationNumber || ""),
                    businessName: business?.name || business?.businessName || "",
                    tin: String(taxValue || ""),
                    taxIdentificationType: business?.EIN ? "EIN" : "TIN",
                    registrationOrTaxIdType: registrationNumber ? "registrationNumber" : "taxIdentificationNumber",
                    natureOfBusiness: nature,
                    businessDescription:
                        business?.businessDescription || business?.description || "",
                    engagesInHighRiskActivities: business?.isHighRisk ? "yes" : "no",
                    highRiskActivities: Array.isArray(business?.activities) ? business.activities : [],
                });
            } catch (err) {
                if (!ignore) setError(err?.message || "Unable to load business details.");
            }
        };
        prefillBusiness();
        return () => { ignore = true; };
    }, [businessId, shouldReturnToSummary]);



    const handleChange = (field, value) => {
        setValidationErrors((current) => {
            if (!current[field]) return current;
            const next = { ...current };
            delete next[field];
            return next;
        });
        const nextFormData = {
            ...formData,
            [field]: value,
        };

        if (field === "country") {
            const selectedCountry = countryOptions.find((country) => country.value === value);
            nextFormData.countryCode = selectedCountry?.code || "";

            if (selectedCountry?.code === "NG" || selectedCountry?.value === "Nigeria") {
                nextFormData.tin = "";
                nextFormData.taxIdentificationType = "EIN";
            }
        }

        if (field === "engagesInHighRiskActivities" && value === "no") {
            nextFormData.highRiskActivities = [];
        }

        setFormData(nextFormData);

        if (field === "rcNumber" || field === "country") {
            const rule = rcNumberValidationRules[nextFormData.country];
            const rcNumberValue = field === "rcNumber" ? value : nextFormData.rcNumber;

            if (rule && rcNumberValue && !rule.regex.test(rcNumberValue.trim())) {
                setRcNumberError(rule.message);
            } else {
                setRcNumberError("");
            }
        }

        if (["tin", "country", "taxIdentificationType"].includes(field)) {
            const shouldValidateEin =
                nextFormData.country === "United States" &&
                nextFormData.taxIdentificationType === "EIN";
            const rule = shouldValidateEin ? tinValidationRules[nextFormData.country] : null;
            const tinValue = field === "tin" ? value : nextFormData.tin;

            if (rule && tinValue && !rule.regex.test(tinValue)) {
                setTinError(rule.message);
            } else {
                setTinError("");
            }
        }
    };

    const isNigeria = formData.country === "Nigeria" || formData.countryCode === "NG";
    const isUS = formData.country === "United States";
    const isLLC = formData.businessType === "LLC";
    const selectedCountry = countryOptions.find((country) => country.value === formData.country);
    const middleEastContinents = ["ME", "MENA", "Middle East", "Middle-East"];
    const middleEastCountryCodes = [
        "AE",
        "BH",
        "CY",
        "EG",
        "IL",
        "IQ",
        "IR",
        "JO",
        "KW",
        "LB",
        "OM",
        "PS",
        "QA",
        "SA",
        "SY",
        "TR",
        "YE",
    ];
    const middleEastCountryNames = [
        "United Arab Emirates",
        "Bahrain",
        "Cyprus",
        "Egypt",
        "Israel",
        "Iraq",
        "Iran",
        "Jordan",
        "Kuwait",
        "Lebanon",
        "Oman",
        "Palestine",
        "Qatar",
        "Saudi Arabia",
        "Syria",
        "Turkey",
        "Yemen",
    ];
    const tinRequiredContinents = ["AF", "EU", "SA", "Africa", "Europe", "South America"];
    const employeeOrTaxIdContinents = [
        "NA",
        "AS",
        "OC",
        "North America",
        "Asia",
        "Oceania",
        "Australia",
    ];
    const isMiddleEast =
        middleEastContinents.includes(selectedCountry?.continent) ||
        middleEastCountryCodes.includes(selectedCountry?.code) ||
        middleEastCountryNames.includes(selectedCountry?.value);
    const isTinRequiredByRegion = tinRequiredContinents.includes(selectedCountry?.continent);
    const isEmployeeOrTaxIdRequired =
        !isMiddleEast &&
        (employeeOrTaxIdContinents.includes(selectedCountry?.continent) ||
            selectedCountry?.value === "Australia");

    const isTinRequired =
        !isNigeria &&
        !isMiddleEast &&
        (isTinRequiredByRegion || isEmployeeOrTaxIdRequired || (isUS && isLLC));

    const tinLabel = isEmployeeOrTaxIdRequired
        ? "Employer or Tax Identification Number"
        : "Tax Identification Number";
    const tinPlaceholder = isEmployeeOrTaxIdRequired
        ? formData.taxIdentificationType === "EIN"
            ? "Enter employer identification number"
            : "Enter tax identification number"
        : isNigeria
        ? "Enter TIN (8-12 digits)"
        : "Enter tax identification number";

    const rcNumberValidationRules = {
        Nigeria: {
            regex: /^\d{6,7}$/,
            message: "RC number must be between 6 to 7 digits.",
        },
        "United States": {
            regex: /^[A-Z0-9-]{8,20}$/i,
            message: "Enter a valid registration number (alphanumeric, 8–20 characters).",
        },
        "United Kingdom": {
            regex: /^[A-Z0-9][A-Z0-9 -]{5,19}$/i,
            message: "Enter a valid registration number (letters and/or numbers, 6-20 characters).",
        },
    };

    const tinValidationRules = {
        Nigeria: {
            regex: /^\d{8,12}$/,
            message: "TIN must be between 8 to 12 digits.",
        },
        "United States": {
            regex: /^\d{2}-\d{7}$/, // EIN format: 12-3456789
            message: "Enter a valid EIN (e.g. 12-3456789).",
        },
    };

    // Disabled state
    const isContinueDisabled =
        !formData.country ||
        !formData.businessName ||
        !formData.businessType ||
        !formData.natureOfBusiness ||
        !formData.businessDescription.trim() ||
        !formData.rcNumber ||
        !formData.engagesInHighRiskActivities ||
        (formData.engagesInHighRiskActivities === "yes" &&
            formData.highRiskActivities.length === 0) ||
        (isTinRequired && !formData.tin) ||
        rcNumberError ||
        tinError;

    const handleInvalidSubmit = () => {
        if (loading) return;

        const requiredErrors = {};
        if (!formData.country) requiredErrors.country = "This field is required.";
        if (!formData.businessType) requiredErrors.businessType = "This field is required.";
        if (!formData.rcNumber) requiredErrors.rcNumber = "This field is required.";
        if (isTinRequired && !formData.tin) requiredErrors.tin = "This field is required.";
        if (!formData.businessName) requiredErrors.businessName = "This field is required.";
        if (!formData.natureOfBusiness) {
            requiredErrors.natureOfBusiness = "This field is required.";
        }
        if (!formData.businessDescription.trim()) {
            requiredErrors.businessDescription = "This field is required.";
        }
        if (!formData.engagesInHighRiskActivities) {
            requiredErrors.engagesInHighRiskActivities = "This field is required.";
        }
        if (
            formData.engagesInHighRiskActivities === "yes" &&
            formData.highRiskActivities.length === 0
        ) {
            requiredErrors.highRiskActivities = "This field is required.";
        }
        if (formData.rcNumber && rcNumberError) requiredErrors.rcNumber = rcNumberError;
        if (formData.tin && tinError) requiredErrors.tin = tinError;

        setValidationErrors(requiredErrors);
        const firstInvalidField =
            Object.keys(requiredErrors)[0] || (rcNumberError ? "rcNumber" : "tin");

        requestAnimationFrame(() => {
            const field = document.querySelector(
                `[data-onboarding-field="${firstInvalidField}"]`
            );
            field?.scrollIntoView({ behavior: "smooth", block: "center" });
            field?.querySelector("input, textarea, button")?.focus({ preventScroll: true });
        });
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError("");

        const selectedNatureOfBusiness = natureOfBusinessOptions.find(
            (option) => option.value === formData.natureOfBusiness
        );
        const isHighRisk = formData.engagesInHighRiskActivities === "yes";
        const data = {
            businessCountry: {
                name: selectedCountry?.value || formData.country,
                code: selectedCountry?.code || formData.countryCode,
                flag: selectedCountry?.imageUrl,
                continent: selectedCountry?.continent,
            },
            name: formData.businessName,
            type: formData.businessType === "LLC" ? "LLC" : "SoleProprietorship",
            natureOfBusiness: {
                nature: selectedNatureOfBusiness?.nature || formData.natureOfBusiness,
                code: selectedNatureOfBusiness?.code || formData.natureOfBusiness,
            },
            businessDescription: formData.businessDescription.trim(),
            isHighRisk,
            activities: isHighRisk ? formData.highRiskActivities : [],
        };

        if (isMiddleEast && formData.registrationOrTaxIdType === "taxIdentificationNumber") {
            data.TIN = formData.rcNumber;
        } else {
            data.registrationNumber = formData.rcNumber;
        }

        if (isTinRequired && formData.tin) {
            data[formData.taxIdentificationType === "EIN" ? "EIN" : "TIN"] = formData.tin;
        }

        try {
            if (shouldReturnToSummary && businessId) {
                await updateBusinessDetails({ businessId, body: data });
                await checkAutoLogin(dispatch);
                navigate("/onboarding/summary");
                return;
            }

            const result = await handleCreateBusiness(data);
            const newBusinessId = result?._id || result?.data?._id;

            if (!newBusinessId) {
                throw new Error(result?.message || "Failed to create business. Please try again.");
            }

            // Switch business
            await handleSwitchCurrentBusiness(newBusinessId);

            // Update Redux store
            await checkAutoLogin(dispatch);

            navigate(shouldReturnToSummary ? "/onboarding/summary" : "/onboarding/contact-details");
        } catch (err) {
            setError(err?.message || "Failed to create business. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <OnboardingLayout
            title="Activate account"
            steps={steps}
            currentStep={currentStep}
            onSkip={() => navigate("/dashboard")}
        >
            <div className="max-w-[1000px] relative pb-5 justify-center md:pl-96">
                <form className="pb-6">
                    <h3 className="aku-heading-h2-medium text-[#101828]">
                        Tell us about your business
                    </h3>

            <div className="mt-8 space-y-4">
                    <div data-onboarding-field="country">
                        <CountryDropdown
                            value={formData.country}
                            options={countryOptions}
                            onChange={(value) => handleChange("country", value)}
                            loading={countriesLoading}
                            error={validationErrors.country}
                        />
                    </div>

                    {/* Business Type */}
                    <div data-onboarding-field="businessType">
                        <Dropdown
                            value={formData.businessType}
                            label="Business type"
                  labelClassName="aku-body-small-medium !mb-2"
                            placeholder="Select business type"
                            options={businessTypeOptions}
                            onChange={(value) => handleChange("businessType", value)}
                            error={validationErrors.businessType}
                        />
                    </div>

                    {isMiddleEast ? (
                        <div data-onboarding-field="rcNumber">
                  <p className="aku-body-small-medium mb-2 text-left text-[#475467]">
                                Registration or Tax ID Number
                            </p>
                            <div className="flex gap-2">
                                <Dropdown
                                    value={formData.registrationOrTaxIdType}
                                    options={registrationOrTaxIdOptions}
                                    onChange={(value) =>
                                        handleChange("registrationOrTaxIdType", value)
                                    }
                                    className="w-1/2 shrink-0"
                                    dropdownClassName="!min-w-[270px]"
                                />
                                <Input
                                    type="text"
                                    value={formData.rcNumber}
                                    onChange={(e) => handleChange("rcNumber", e)}
                                    placeholder={
                                        formData.registrationOrTaxIdType === "registrationNumber"
                                            ? "Enter registration number"
                                            : "Enter tax ID number"
                                    }
                                    className="!mt-0 flex-1"
                                    error={validationErrors.rcNumber}
                                />
                            </div>
                        </div>
                    ) : (
                        <div data-onboarding-field="rcNumber">
                            <Input
                                label="Business registration number"
                      labelClassName="aku-body-small-medium !mb-2"
                                type="text"
                                value={formData.rcNumber}
                                onChange={(e) => handleChange("rcNumber", e)}
                                placeholder="Enter business registration number"
                                error={validationErrors.rcNumber}
                                className="!mt-0"
                            />
                        </div>
                    )}

                    {isTinRequired && (
                        <>
                            {isEmployeeOrTaxIdRequired ? (
                                <div data-onboarding-field="tin">
                      <p className="aku-body-small-medium mb-2 text-left text-[#475467]">
                                        {tinLabel}
                                    </p>
                                    <div className="flex gap-2">
                                        <Dropdown
                                            value={formData.taxIdentificationType}
                                            options={taxIdentificationTypeOptions}
                                            onChange={(value) =>
                                                handleChange("taxIdentificationType", value)
                                            }
                                            className="w-[168px] shrink-0"
                                            dropdownClassName="!min-w-[168px]"
                                        />
                                        <Input
                                            type="text"
                                            placeholder={tinPlaceholder}
                                            value={formData.tin}
                                            onChange={(e) => handleChange("tin", e)}
                                            className="!mt-0 flex-1"
                                            error={validationErrors.tin}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div data-onboarding-field="tin">
                                    <Input
                                        label={tinLabel}
                          labelClassName="aku-body-small-medium !mb-2"
                                        type={isNigeria ? "number" : "text"}
                                        placeholder={tinPlaceholder}
                                        value={formData.tin}
                                        onChange={(e) => handleChange("tin", e)}
                                        error={validationErrors.tin}
                                        className="!mt-0"
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <div data-onboarding-field="businessName">
                        <Input
                            label="Business name"
                  labelClassName="aku-body-small-medium !mb-2"
                            type="text"
                            value={formData.businessName}
                            onChange={(e) => handleChange("businessName", e)}
                            placeholder="E.g. Chois Logistics"
                            error={validationErrors.businessName}
                            className="!mt-0"
                        />
                    </div>

                    {/* Nature of Business */}
                    <div data-onboarding-field="natureOfBusiness">
                        <Dropdown
                            value={formData.natureOfBusiness}
                            label="Nature of business"
                  labelClassName="aku-body-small-medium !mb-2"
                            placeholder="Please select nature of business"
                            options={natureOfBusinessOptions}
                            onChange={(value) => handleChange("natureOfBusiness", value)}
                            error={validationErrors.natureOfBusiness}
                        />
                    </div>

                    <div data-onboarding-field="businessDescription">
                <label className="aku-body-small-medium mb-2 block text-[#475467]">
                            Business description
                        </label>
                        <textarea
                            value={formData.businessDescription}
                            onChange={(event) =>
                                handleChange("businessDescription", event.target.value)
                            }
                            placeholder="Describe your business"
                            rows={4}
                            className={`aku-body-normal-regular min-h-[112px] w-full resize-none rounded-lg px-4 py-4 text-[#101828] outline-none placeholder:text-[#7A80AE] ${
                                validationErrors.businessDescription
                                    ? "bg-[#FFF1F3] ring-1 ring-[#F04438]"
                                    : "bg-[#f1f5f9]"
                            }`}
                        />
                        {validationErrors.businessDescription && (
                            <p className="mt-1 text-xs text-[#D92D20]">
                                {validationErrors.businessDescription}
                            </p>
                        )}
                    </div>

                    <div data-onboarding-field="engagesInHighRiskActivities">
                        <p className="aku-body-small-medium text-[#101828]">
                            Does your business engage in high-risk activities?
                        </p>
                        <p className="text-sm text-[#6F739B] mt-2 max-w-[650px]">
                            If your business is involved in any high-risk activities (e.g., crypto
                            trading, gambling, adult content, money lending, etc.), please disclose
                            them here.
                        </p>

                        <div className="flex items-center gap-8 mt-4">
                            {[
                                { label: "Yes", value: "yes" },
                                { label: "No", value: "no" },
                            ].map((option) => (
                                <label
                                    key={option.value}
                                    className="aku-body-small-medium flex items-center gap-2 text-[#101828] cursor-pointer"
                                >
                                    <input
                                        type="radio"
                                        name="engagesInHighRiskActivities"
                                        value={option.value}
                                        checked={
                                            formData.engagesInHighRiskActivities === option.value
                                        }
                                        onChange={() =>
                                            handleChange(
                                                "engagesInHighRiskActivities",
                                                option.value
                                            )
                                        }
                                        className="size-4 accent-[#6F00FF]"
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                        {validationErrors.engagesInHighRiskActivities && (
                            <p className="mt-1 text-xs text-[#D92D20]">
                                {validationErrors.engagesInHighRiskActivities}
                            </p>
                        )}
                    </div>

                    {formData.engagesInHighRiskActivities === "yes" && (
                        <div
                            data-onboarding-field="highRiskActivities"
                            className={
                                validationErrors.highRiskActivities
                                    ? "rounded-lg ring-1 ring-[#F04438]"
                                    : ""
                            }
                        >
                            <MultiSelectDropdown
                                instanceId="high-risk-activities-select"
                                label="High-risk activities"
                                placeholder="Select high-risk activities"
                                options={highRiskActivityOptions}
                                value={formData.highRiskActivities}
                                onChange={(value) => handleChange("highRiskActivities", value)}
                            />
                            {validationErrors.highRiskActivities && (
                                <p className="mt-1 text-xs text-[#D92D20]">
                                    {validationErrors.highRiskActivities}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
                    </div>

                    <div className="flex justify-end items-center gap-8 pt-4">
                        <button
                            type="button"
                            onClick={onBack}
                            className="text-sm font-semibold text-[#101828]"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={isContinueDisabled ? handleInvalidSubmit : handleSubmit}
                            disabled={loading}
                            aria-disabled={isContinueDisabled || loading}
                            className={`rounded-lg px-6 py-3 text-sm font-semibold ${
                                isContinueDisabled || loading
                                    ? "bg-[#E9D7FE] text-white cursor-not-allowed"
                                    : "bg-[#7F56D9] text-white hover:bg-[#6f00ff]"
                            }`}
                        >
                            {loading ? <Loader /> : "Save and continue"}
                        </button>
                    </div>
                </form>
            </div>
        </OnboardingLayout>
    );
};

export default BusinessDetails;
